import { z } from "zod";
import type { PlanTier } from "./plan-tiers";

export const CreditTypeSchema = z.enum(["IMAGE_TO_TEXT", "IMAGE_GENERATION"]);

export type CreditType = z.infer<typeof CreditTypeSchema>;

export interface CreditLimit {
	type: CreditType;
	amount: number;
	period: "daily" | "monthly" | "one-time";
}

export interface PlanCredits {
	name: string;
	price: number;
	currency: string;
	credits: CreditLimit[];
}

export const plans: Record<string, PlanCredits> = {
	free: {
		name: "Free",
		price: 0,
		currency: "USD",
		credits: [
			{
				type: "IMAGE_TO_TEXT",
				amount: 10,
				period: "one-time",
			},
			{
				// 注册送一次 15，不再每日发放（2026-09-14 定一次性，2026-09-17 由 20 降到 12，
				// 2026-10-05 升到 15：SD2 / 首尾帧页默认 Seedance 2.0 Mini 480p×4 秒 = 15 分，免费号能做一条）。
				// 台账回放显示 74% 的付费发生在注册 24 小时内，回访日的每日额度只贡献成本。
				// 降到 12 是为了把撞墙提前：配合默认模型 v3.0（6 分/张），12 = 2 张图（15 仍是 2 张）；
				// 视频 Fast 3.0 默认 480p×5 秒 12 分，一条用完就撞墙。改前发 20 的实测是
				// 人均只消耗 5.95 分、23.6% 撞墙，六张的额度对多数人是发不完的浪费。
				// 只对新建的 UserCredit 行生效：totalCredits 在建行时落库，老免费号不补发。
				// 删号冷静期与一次性邮箱的拦截在 initializeUserCredits。
				type: "IMAGE_GENERATION",
				amount: 15,
				period: "one-time",
			},
		],
	},
	starter: {
		name: "Starter",
		price: 9.9,
		currency: "USD",
		credits: [
			{
				type: "IMAGE_TO_TEXT",
				amount: Number.POSITIVE_INFINITY,
				period: "monthly",
			},
			{
				type: "IMAGE_GENERATION",
				amount: 450,
				period: "monthly",
			},
		],
	},
	pro: {
		name: "Pro",
		price: 19.9,
		currency: "USD",
		credits: [
			{
				type: "IMAGE_TO_TEXT",
				amount: Number.POSITIVE_INFINITY,
				period: "monthly",
			},
			{
				type: "IMAGE_GENERATION",
				amount: 1350,
				period: "monthly",
			},
		],
	},
	// 2026-09-12 启用。价格从 Creem 时代注释里的 $29.99 / 2500 积分改掉了——按当时
	// 每积分 $0.008 的成本口径，那组数字的成本占比是 67%（年付 80%），是倒贴。
	// 每积分比 Pro 只便宜 6%：这一档卖的是上限（image-to-video 的 Master 档一条
	// 就要上百积分），不是单价。
	//
	// 2026-09-14 全站积分改成与 Kie 1:1（每积分 $0.005 上游成本），图片每张的积分
	// 涨了 1.5~1.67 倍。为了不让订阅用户被暗涨价，三档额度同步 ×1.5：300/900/2400
	// → 450/1350/3600，成本占比 22.7% / 33.9% / 36.1%，与改前的 24/36/38% 持平。
	ultimate: {
		name: "Ultimate",
		price: 49.9,
		currency: "USD",
		credits: [
			{
				type: "IMAGE_TO_TEXT",
				amount: Number.POSITIVE_INFINITY,
				period: "monthly",
			},
			{
				type: "IMAGE_GENERATION",
				amount: 3600,
				period: "monthly",
			},
		],
	},
};

/**
 * 删号冷静期（天）。冷静期内同邮箱重新注册的账号不发放任何免费额度
 * （每日额度 + 一次性额度），防止「删号→重注册」无限薅免费 credits。
 */
export const DELETED_ACCOUNT_FREE_CREDITS_COOLDOWN_DAYS = 30;

/**
 * 一次性 credit pack（B 方案：独立台账）。买断制、无自动续费、永久有效
 * （2026-09-29 起，CreditGrant.expiresAt 写 null；之前是 30 天）。
 * per-credit 均高于订阅 Starter，不抢订阅。
 * productId 按环境注入：dev 读 test 产品，prod 读 live 产品。
 */
export interface CreditPack {
	id: string;
	productId: string;
	price: number;
	currency: string;
	credits: number;
	creditType: CreditType;
	/**
	 * 只买过积分包、没订阅（或订阅档更低）的用户按买过的包里最高的这一档算，
	 * 决定视频分辨率等按档位切的能力。去水印不看这个，买过任意包都永久去水印。
	 */
	tier: PlanTier;
	/**
	 * 人民币价格。Waffo 的一次性商品用 CNY 结账时收银台只出微信支付（USD 出
	 * 卡 / Apple Pay / Google Pay），所以这是中国大陆用户唯一能走的通道。
	 * 只在简体中文界面上作为第二个购买入口展示。
	 */
	priceCNY: number;
	/**
	 * 已下架：定价页不再展示，但必须留在列表里——去水印是买断的永久权益，
	 * helper.ts 靠 getCreditPackByProductId 认历史 ONE_TIME 订单，legacy-products
	 * 的 Creem 映射也指向它；整条删掉，买过这个包的人会当场失去去水印权限。
	 * 对应的 NEXT_PUBLIC_PRODUCT_ID_* env 同样不能从 Vercel 删。
	 */
	retired?: boolean;
}

export const creditPacks: CreditPack[] = [
	{
		// 2026-09-27 下架：$6.90 拉低了客单价，最低档改为 480/$16.90。
		// tier 仍是 pro：9/27 起所有包买家都按 Pro 算，历史买家不能因为分档倒退。
		id: "pack_small",
		retired: true,
		productId: process.env.NEXT_PUBLIC_PRODUCT_ID_PACK_SMALL ?? "",
		price: 6.9,
		currency: "USD",
		priceCNY: 49,
		credits: 180,
		creditType: "IMAGE_GENERATION",
		tier: "pro",
	},
	{
		// 2026-09-29 加：入门包，撞墙后不想订阅的人的最低门槛。只解锁到 Starter 档
		// （720p）——一次性就永久拿 1080p/4K 会比 Starter 订阅权限还高。
		// 2026-09-30 由 250/$9.90 改回 180/$6.90（同产品 ID，Waffo 生产版本同步改价）：
		// 9/20–9/24 收入 ~$65/天主要靠 $6.90 小包的新买家，9/24 默认月付 + 9/27 下架
		// 小包后掉到 ~$37/天，$9.90 三天只卖出 1 单。每积分 $0.038，仍比 600 包贵。
		// 不直接复活 pack_small：它 9/27 起是 pro 档，改回 starter 会让老买家权限倒退。
		// 2026-10-25 下架，最低档变成 600/$19.90。tier 保持 starter，老买家权限不变。
		id: "pack_mini",
		retired: true,
		productId: process.env.NEXT_PUBLIC_PRODUCT_ID_PACK_MINI ?? "",
		price: 6.9,
		currency: "USD",
		priceCNY: 49,
		credits: 180,
		creditType: "IMAGE_GENERATION",
		tier: "starter",
	},
	{
		// 2026-09-27 由 480/$16.90 改为 600/$19.90：一次性包改成 $19.90 起、每档 +$20
		// 对齐竞品阶梯；积分同步加到 600 让每积分价不涨。产品 ID 不变，Waffo 生产版本
		// 已改价改名。
		id: "pack_large",
		productId: process.env.NEXT_PUBLIC_PRODUCT_ID_PACK_LARGE ?? "",
		price: 19.9,
		currency: "USD",
		priceCNY: 139,
		credits: 600,
		creditType: "IMAGE_GENERATION",
		tier: "pro",
	},
	{
		// 2026-09-12 加：image-to-video 上线时 Master 3.0 一条 720p 要 205 积分、
		// 1080p 要 510，小包和大包都买不起一条；whale 曾一天连买 9 个小包硬凑。
		// 每积分 $0.027，仍比 Pro 订阅（$0.015）贵 80%，不抢订阅。
		// 2026-09-14 三个包随套餐一起 ×1.5（120/320/1000 → 180/480/1500），原因见上面
		// ultimate 的注释。Waffo 产品名带积分数（"120 Credits Pack"），改数要同步改名。
		id: "pack_xl",
		productId: process.env.NEXT_PUBLIC_PRODUCT_ID_PACK_XL ?? "",
		price: 39.9,
		currency: "USD",
		priceCNY: 279,
		credits: 1500,
		creditType: "IMAGE_GENERATION",
		tier: "pro",
	},
	{
		// 2026-09-27 加：阶梯第三档（$19.90 / $39.90 / $59.90）。每积分 $0.024，
		// 仍比 Ultimate 月付（$0.014）贵 70%，不抢订阅。
		id: "pack_xxl",
		productId: process.env.NEXT_PUBLIC_PRODUCT_ID_PACK_XXL ?? "",
		price: 59.9,
		currency: "USD",
		priceCNY: 419,
		credits: 2500,
		creditType: "IMAGE_GENERATION",
		tier: "pro",
	},
];

export const getCreditPackByProductId = (
	productId: string,
): CreditPack | undefined =>
	creditPacks.find((pack) => pack.productId && pack.productId === productId);

export const getPlanCredits = (planId: string): PlanCredits => {
	const plan = plans[planId];
	if (!plan) {
		throw new Error(`Plan ${planId} not found`);
	}
	return plan;
};

export const getCreditLimit = (
	planId: string,
	creditType: CreditType,
): CreditLimit => {
	const plan = getPlanCredits(planId);
	const credit = plan.credits.find((c) => c.type === creditType);
	if (!credit) {
		throw new Error(
			`Credit type ${creditType} not found for plan ${planId}`,
		);
	}
	return credit;
};

/**
 * Per-plan IMAGE_GENERATION credits granted during the 3-day yearly trial.
 * Fewer than the full monthly allowance on purpose — lets users sample the
 * product without abusing the trial. Keyed by planType (starter / pro / ...).
 * Add new keys here as you enable trials on more plans.
 */
export const TRIAL_IMAGE_GENERATION_CREDITS_BY_PLAN: Record<string, number> = {
	starter: 75,
	pro: 180,
	ultimate: 375,
};

/**
 * Fallback used only when a plan is missing from TRIAL_IMAGE_GENERATION_CREDITS_BY_PLAN
 * (e.g. a new plan is wired to trial without an explicit value). Keep conservative.
 */
export const DEFAULT_TRIAL_IMAGE_GENERATION_CREDITS = 50;

export const getTrialImageGenerationCredits = (planType: string): number =>
	TRIAL_IMAGE_GENERATION_CREDITS_BY_PLAN[planType] ??
	DEFAULT_TRIAL_IMAGE_GENERATION_CREDITS;

/**
 * @deprecated Kept so older log-site imports still compile. Use
 * `getTrialImageGenerationCredits(planType)` for per-plan values.
 */
export const TRIAL_IMAGE_GENERATION_CREDITS =
	DEFAULT_TRIAL_IMAGE_GENERATION_CREDITS;

export const getTrialCredits = (planType: string): CreditLimit[] => [
	{
		type: "IMAGE_TO_TEXT",
		amount: Number.POSITIVE_INFINITY,
		period: "monthly",
	},
	{
		type: "IMAGE_GENERATION",
		amount: getTrialImageGenerationCredits(planType),
		period: "monthly",
	},
];
